// utils/enemyAI.js

/**
 * Calculate Manhattan distance between two points
 */
const distance = (x1, y1, x2, y2) => {
  return Math.abs(x1 - x2) + Math.abs(y1 - y2);
};

/**
 * Find the nearest target from a list of potential targets
 */
const findNearestTarget = (unit, targets) => {
  if (targets.length === 0) return null;
  
  return targets.reduce((nearest, target) => {
    const dist = distance(unit.x, unit.y, target.x, target.y);
    const nearestDist = distance(unit.x, unit.y, nearest.x, nearest.y);
    return dist < nearestDist ? target : nearest;
  }, targets[0]);
};

/**
 * Attempt to shoot at a target
 * Returns an action object describing what happened
 */
const attemptShoot = (enemy, target, hasLineOfSight, getCoverBonus) => {
  const dist = distance(enemy.x, enemy.y, target.x, target.y);
  
  // Check if target is in range and has line of sight
  if (dist > 6 || !hasLineOfSight(enemy.x, enemy.y, target.x, target.y)) {
    return null;
  }
  
  const coverBonus = getCoverBonus(target.x, target.y);
  const hitChance = Math.max(20, 70 - dist * 5 - coverBonus);
  const hit = Math.random() * 100 < hitChance;
  
  if (hit) {
    const damage = Math.floor(Math.random() * 2) + 1;
    return {
      type: 'shoot',
      enemyId: enemy.id,
      targetId: target.id,
      hit: true,
      damage,
      message: `🔴 ${enemy.name} hits ${target.name} for ${damage} damage!`
    };
  } else {
    return {
      type: 'shoot',
      enemyId: enemy.id,
      targetId: target.id,
      hit: false,
      message: `🔴 ${enemy.name} missed ${target.name}!`
    };
  }
};

/**
 * Attempt to move towards a target
 * Returns an action object describing the move
 */
const attemptMove = (enemy, target, getUnitAt) => {
  const dx = Math.sign(target.x - enemy.x);
  const dy = Math.sign(target.y - enemy.y);
  const newX = enemy.x + dx;
  const newY = enemy.y + dy;
  
  // Check if the target position is occupied
  if (!getUnitAt(newX, newY)) {
    return {
      type: 'move',
      enemyId: enemy.id,
      newX,
      newY,
      message: `🔴 ${enemy.name} moves closer`
    };
  }
  
  // Can't move, return null
  return null;
};

/**
 * Execute enemy AI turn for a single enemy unit
 * Returns an action object or null if no valid action
 */
export const executeEnemyAction = (enemy, targets, hasLineOfSight, getCoverBonus, getUnitAt) => {
  if (targets.length === 0) return null;
  
  const nearestTarget = findNearestTarget(enemy, targets);
  if (!nearestTarget) return null;
  
  // Try to shoot first if possible
  const shootAction = attemptShoot(enemy, nearestTarget, hasLineOfSight, getCoverBonus);
  if (shootAction) return shootAction;
  
  // If can't shoot, try to move closer
  const moveAction = attemptMove(enemy, nearestTarget, getUnitAt);
  if (moveAction) return moveAction;
  
  // No valid action, skip turn
  return {
    type: 'skip',
    enemyId: enemy.id,
    message: null
  };
};

/**
 * Process the result of an enemy action and update units
 */
export const applyEnemyAction = (action, units) => {
  if (!action) return units;
  
  switch (action.type) {
    case 'shoot':
      return units.map(u => {
        if (u.id === action.targetId && action.hit) {
          return { ...u, hp: Math.max(0, u.hp - action.damage) };
        }
        if (u.id === action.enemyId) {
          return { ...u, ap: 0 };
        }
        return u;
      });
      
    case 'move':
      return units.map(u => 
        u.id === action.enemyId 
          ? { ...u, x: action.newX, y: action.newY, ap: 0 }
          : u
      );
      
    case 'skip':
      return units.map(u => 
        u.id === action.enemyId 
          ? { ...u, ap: 0 }
          : u
      );
      
    default:
      return units;
  }
};

